import { trigger, transition, style, animate } from '@angular/animations';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService } from 'app/auth/auth.service';
import { PaymentService } from 'app/components/payments/service/payment.service';
import { IDocument } from 'app/models/documents/document';
import { ICurriculumCoreElement } from 'app/models/training/curriculum/curriculum-core-element';
import { ICurriculumPageReference } from 'app/models/training/curriculum/curriculum-page-reference';
import { ITrainingProgram } from 'app/models/training/training-program';
import { ITrainingProgramContact } from 'app/models/training/training-program-contact';
import { ITrainingProgramDocument } from 'app/models/training/training-program-document';
import { ITrainingProgramRevocation } from 'app/models/training/training-program-revocation';
import { SharedService } from 'app/services/core/shared.service';
import { TrainingProgramService } from 'app/services/training/training-program.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable } from 'rxjs';
import { PrintTrainingProgramComponent } from './print-training-program/print-training-program.component';
import { FacilitatorLicenseIdValidator } from '@shared/validators/facilitator-id-validator';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { Router } from '@angular/router';
import moment from 'moment';

export interface ICurriculumUpdateDialog {
  curriculumUpdateTypeId: number;
  id: number;
}

export interface ITrainingProgramSnapshotDialogData {
  curriculumMap: ICurriculumCoreElement[];
  curriculumPageKey: { [referenceId: string]: string };
  trainingProgram: ITrainingProgram;
}


@Component({
    selector: 'app-training-program',
    templateUrl: './training-program.component.html',
    styleUrls: ['./training-program.component.scss'],
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ transform: 'translateX(-100%)' }),
                animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
            ])
        ])
    ],
    standalone: false
})

export class TrainingProgramComponent implements OnInit {
  public loadingTrainingProgram: boolean = false;
  public showEditLoading: boolean = false;
  public renewing: boolean = false;
  public startedRenewal: boolean = false;
  public licenseLocked: boolean = false;
  public renewalErrorSubmitting: boolean = false;
  public showStartButton: boolean = false;
  public saving: boolean = false;
  private page: string = 'training';
  public step: number = 0;
  public programInfoValid: boolean = false;
  public contactsValid: boolean = true;
  public curriculumValid: boolean = true;
  public worksheetComplete: boolean = true;
  public showCloseWarning: boolean = false;
  public uploading: boolean = false;
  public downloading: boolean = false;
  public deleting: boolean = false;
  public viewingDetails: boolean = false;
  public hasGrantedProgram: boolean = false;
  public hasActiveRevocation: boolean = false;
  public hasCurriculumUpdate: boolean = false;
  public cardColor: string = this.sharedService.blue + '20';
  public activeDocuments: ITrainingProgramDocument[] = [];
  public curriculumComments: { [id: number]: string } = {};
  public curriculumId: number = 0;
  public curriculumTypeId: number = 0;
  public curriculumUpdateTypeId: number = 0;
  public deniedCurriculumTypeId: number = 0;
  public deprecatedCurriculumTypeId: number = 0;
  public showConfirmation: boolean = false;
  public licenseSnapshot: string = "License Snapshot";
  public licenseSnapshotType: number = 0;

  public trainingProgramSteps: string[] = [
    "startingForm",
    "programInfoForm",
    "contactsForm",
    "documentsForm",
    "curriculumPagesForm",
    "feeForm",
    "verifyForm"
  ];
  public stepsIndex: { [id: string]: number } = {
    "startingForm": 0,
    "programInfoForm": 1,
    "contactsForm": 2,
    "documentsForm": 3,
    "curriculumPagesForm": 4,
    "feeForm": 5,
    "verifyForm": 6
  };
  public programLocked: boolean = false;
  public trainingPrograms: ITrainingProgram[] = [];
  public trainingProgram: ITrainingProgram = {
    id: 0,
    programId: '',
    userId: '',
    status: '',
    renewalCount: '',
    assignedTo: null,
    programName: '',
    headquartersStreet: '',
    headquartersCity: '',
    headquartersState: '',
    headquartersZip: '',
    headquartersCounty: '',
    mailingStreet: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    mailingCounty: '',
    facilityStreet: '',
    facilityCity: '',
    facilityState: '',
    facilityZip: '',
    facilityCounty: '',
    registeredIn: '',
    website: '',
    confirmation: false,
    curriculumAssertion: false,
    electronicSignature: '',
    isRenewal: false,
    canRenew: false,
    renewalAssertion: false,
    paymentAssertion: false,
    applicationDate: '',
    grantedDate: '',
    approvedBy: '',
    expirationDate: '',
    trainingProgramContacts: [],
    trainingProgramDocuments: [],
    trainingProgramRevocations: [],
    mailingIsHeadquarters: false,
    facilityIsHeadquarters: false,
    facilityIsMailing: false,
    payments: [],
    supervisor: '',
    continuingEducation: false,
    facilitatorName: '',
    facilitatorLicense: '',
    dateCreated: '',
    trainingProgramInformationCorrect: null,
    trainingProgramInformationCorrectReason: '',
    peopleCorrect: null,
    peopleCorrectReason: '',
    renewedDate: '',
    curriculumCorrect: false,
    curriculumWorksheetCorrect: false,
    canOperate: false,
    secretaryofStateNumber: '',
    closedDate: '',
  };
  public curriculumMap: ICurriculumCoreElement[] = [];
  public referenceIds: string[] = [];
  public curriculumPageKey: { [referenceId: string]: string } = {};
  public startingForm = new UntypedFormGroup({});
  public contactAttestationForm = new UntypedFormGroup({
    peopleCorrect: new UntypedFormControl(false),
    peopleCorrectReason: new UntypedFormControl(''),
  }, {
    validators: [
      this.peopleCorrectValidator()
    ]
  });
  public programInfoForm = new UntypedFormGroup({
    programName: new UntypedFormControl("", [Validators.required]),
    headquartersStreet: new UntypedFormControl("", [Validators.required]),
    headquartersCity: new UntypedFormControl("", [Validators.required]),
    headquartersState: new UntypedFormControl("", [Validators.required]),
    headquartersZip: new UntypedFormControl("", [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    headquartersCounty: new UntypedFormControl(""),
    mailingIsHeadquarters: new UntypedFormControl(false),
    mailingStreet: new UntypedFormControl(""),
    mailingCity: new UntypedFormControl(""),
    mailingState: new UntypedFormControl(""),
    mailingZip: new UntypedFormControl("", [Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    mailingCounty: new UntypedFormControl(""),
    facilityIsHeadquarters: new UntypedFormControl(false),
    facilityIsMailing: new UntypedFormControl(false),
    facilityStreet: new UntypedFormControl(""),
    facilityCity: new UntypedFormControl(""),
    facilityState: new UntypedFormControl(""),
    facilityZip: new UntypedFormControl("", [Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    facilityCounty: new UntypedFormControl(""),
    registeredIn: new UntypedFormControl("", [Validators.required]),
    continuingEducation: new UntypedFormControl(false),
    website: new UntypedFormControl("", [Validators.required,
    Validators.pattern(/^(http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]/i)]),
    trainingProgramInformationCorrect: new UntypedFormControl(false),
    trainingProgramInformationCorrectReason: new UntypedFormControl(''),
    secretaryofStateNumber: new UntypedFormControl('', [Validators.required])

  }, {
    validators: [this.mailingStreetValidator(),
    this.mailingCityValidator(),
    this.mailingStateValidator(),
    this.mailingZipValidator(),
    this.facilityStreetValidator(),
    this.facilityCityValidator(),
    this.facilityStateValidator(),
    this.facilityZipValidator(),
    this.trainingProgramInformationCorrectValidator()
    ]
  });
  public contactsForm = new UntypedFormGroup({});
  public documentsForm = new UntypedFormGroup({
    curriculumHoursAssertion: new UntypedFormControl(false, [Validators.requiredTrue]),
    curriculumModulesAssertion: new UntypedFormControl(false, [Validators.requiredTrue]),
    curriculumPracticumAssertion: new UntypedFormControl(false, [Validators.requiredTrue]),
    curriculumAcceleratedAssertion: new UntypedFormControl(false, [Validators.requiredTrue]),
    curriculumNoticeAssertion: new UntypedFormControl(false, [Validators.requiredTrue]),
    curriculumCorrect: new UntypedFormControl(false),
  }, {
    validators: [
      this.curriculumCorrectValidator()
    ]
  });
  public curriculumPagesForm = new UntypedFormGroup({});
  public paymentForm = new UntypedFormGroup({
  });
  public curriculumWorksheetForm = new UntypedFormGroup({
    curriculumWorksheetCorrect: new UntypedFormControl(false),
  }, {
    validators: [
      this.curriculumWorksheetCorrectValidator()
    ]
  });
  public verifyForm = new UntypedFormGroup({
    responsibleAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    informationAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    publicRecordAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    usAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    recordKeepingAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    programCompletionAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    termAttestation: new UntypedFormControl(false, [Validators.requiredTrue]),
    electronicSignature: new UntypedFormControl('', [Validators.required]),
    paymentAssertion: new UntypedFormControl(false, [Validators.requiredTrue])
  });

  public facilitatorForm = new UntypedFormGroup({
    facilitatorName: new UntypedFormControl(''),
    facilitatorLicense: new UntypedFormControl('', {
      asyncValidators: [this.licenseIdValidator.validate.bind(this.licenseIdValidator)],
      updateOn: 'blur'
    })
  }, { validators: [this.facilitatorValidator()] });

  public canApplyStatuses: string[] = [this.sharedService.denied, this.sharedService.expired, this.sharedService.withdrawn, this.sharedService.revoked, this.sharedService.closed, this.sharedService.surrendered];
  public paymentType = "TrainingProgram";

  public displayedColumns: string[] = ['firstName', 'lastName', 'title', 'role', 'email', 'phone', 'actions'];
  public reviewColumns: string[] = ['firstName', 'lastName', 'title', 'role', 'email', 'phone'];
  public dataSource = new MatTableDataSource<ITrainingProgramContact>(this.trainingProgram.trainingProgramContacts);
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  @ViewChild('sort', { static: false }) sort: MatSort;

  public documentColumns: string[] = ['name', 'status', 'comments', 'actions']
  public documentSource = new MatTableDataSource<ITrainingProgramDocument>(this.trainingProgram.trainingProgramDocuments);
  @ViewChild('documentPaginator', { static: false }) documentPaginator: MatPaginator;
  @ViewChild('documentSort', { static: false }) documentSort: MatSort;

  constructor(private trainingProgramService: TrainingProgramService,
    public sharedService: SharedService,
    public authService: AuthService,
    public paymentService: PaymentService,
    private router: Router,
    private dialog: MatDialog,
    public toastr: ToastrService,
    private licenseIdValidator: FacilitatorLicenseIdValidator) { }

  ngOnInit(): void {
    this.getDocumentTypes();
    this.getCurriculumMap()
      .then(() => this.getTrainingPrograms())
      .catch(() => this.sharedService.consoleLog('Get Curriculum Map Failed'));
    this.getUserInfo();
  }

  getTrainingPrograms(): void {
    this.loadingTrainingProgram = true;
    this.trainingProgramService.getTrainingPrograms().subscribe(
      response => {
        this.loadingTrainingProgram = false;
        this.trainingPrograms = response;
        //this.trainingPrograms.forEach(license => license.isRenewal = true);

        if (this.trainingPrograms.length == 0)
          this.showStartButton = true;
        this.hasGrantedProgram = this.trainingPrograms.some(p => p.status === this.sharedService.approved);
        if (this.hasGrantedProgram) {
          let granted = this.trainingPrograms.find(tps => tps.status === this.sharedService.approved);
          this.hasActiveRevocation = granted.trainingProgramRevocations.some(tpr => tpr.active);
        }
        let program: ITrainingProgram = this.trainingPrograms.find(p => p.status === this.sharedService.pending);
        if (program) {
          this.trainingProgram = program;
        }
      },
      error => {
        this.sharedService.consoleLog(error);
        this.loadingTrainingProgram = false;
      },
      () => {
        this.viewingDetails = this.trainingPrograms.length === 0;
        if (this.trainingProgram.id !== 0) {
          this.getProgramInformation();
        }
      }
    );
  }

  startApplication(): void {
    this.showStartButton = false;
    this.sharedService.openConfirm("Would you like to begin a training program curriculum application?");
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.trainingProgramService.createTrainingProgram().subscribe(
          response => this.trainingProgram = response,
          error => console.log('error', error),
          () => {
            this.step = 0;
            this.trainingPrograms.push(this.trainingProgram);
            this.viewingDetails = true;
          }
        );
      }
      else {
        this.showStartButton = true;
      }
    });
  }

  hasPendingFee(program: ITrainingProgram): boolean {
    if (program == null || program.payments == null || program.payments.length == 0) {
      return false;
    }

    let pendingFee = program.payments.find(tp => !tp.paid);

    if (pendingFee == null || pendingFee == undefined) {
      return false;
    }
    return true;
  }

  hasPaidApplicationFee(program: ITrainingProgram): boolean {
    if (program == null || program.payments == null || program.payments.length == 0) {
      return false;
    }

    let appFee = program.payments.find(tp => tp.paid && tp.type == this.paymentService.applicationFeeType);

    if (appFee == null || appFee == undefined) {
      return false;
    }

    return true;
  }

  getAppliationFeeStatus(program: ITrainingProgram): string {
    if (program == null || program.payments == null || program.payments.length == 0) {
      return "";
    }

    let appFee = program.payments.find(tp => tp.type == this.paymentService.applicationFeeType);

    if (appFee == null || appFee == undefined) {
      return "";
    }

    return appFee.status;
  }

  getAppliationFeeCleared(program: ITrainingProgram): string {
    if (program == null || program.payments == null || program.payments.length == 0) {
      return "";
    }

    let appFee = program.payments.find(tp => tp.type == this.paymentService.applicationFeeType);

    if (appFee == null || appFee == undefined) {
      return "";
    }

    return appFee.paymentCleared;
  }

  getUserInfo(): void {
    if (this.sharedService.userProfile.userId === null) {
      this.loadingTrainingProgram = true;
      this.sharedService.getUserInfo().subscribe(
        response => {
          this.sharedService.userProfile = response;
          this.loadingTrainingProgram = false;
        },
        error => {
          this.sharedService.consoleLog(error);
          this.loadingTrainingProgram = false;
        },
        () => {
          this.sharedService.toggleTheme();
          this.updateUserState();
        }
      );
    }
    else {
      this.updateUserState();
    }
  }

  pageTitle(): string {
    if (this.programLocked && this.trainingProgram.isRenewal) {
      return "Edit Facilitator Training Program Curriculum Application Renewal";
    }
    else if (this.programLocked && !this.trainingProgram.isRenewal) {
      return "Edit Facilitator Training Program Curriculum Application";
    }
    else if (!this.programLocked && !this.trainingProgram.isRenewal) {
      return "Submit a Facilitator Training Program Curriculum Application";
    }
    else if (!this.programLocked && this.trainingProgram.isRenewal) {
      return "Submit a Facilitator Training Program Curriculum Application Renewal";
    }
    else {
      return "Training Program";
    }
  }

  public get canApplyAgain(): boolean {
    let canApply: boolean = true;
    this.trainingPrograms.forEach(program => {
      if (!this.canApplyStatuses.includes(program.status)) {
        canApply = false;
      }
    });
    return canApply;
  }

  getIndexLetter(index: number, upper: boolean): string {
    let letterArray: string[] = [
      'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
      'aa', 'bb', 'cc', 'dd', 'ee', 'ff', 'gg', 'hh', 'ii', 'jj', 'kk', 'll', 'mm', 'nn', 'oo', 'pp', 'qq', 'rr', 'ss', 'tt', 'uu', 'vv', 'ww', 'xx', 'yy', 'zz'
    ]
    if (upper) {
      return letterArray[index].toUpperCase();
    }
    return letterArray[index];
  }

  getDocumentTypes(): void {
    this.loadingTrainingProgram = true;
    this.trainingProgramService.getDocumentTypes().subscribe(
      response => {
        this.curriculumTypeId = response.find(dt => dt.type === 'Curriculum').id;
        this.curriculumUpdateTypeId = response.find(dt => dt.type === 'Curriculum Update').id;
        this.deniedCurriculumTypeId = response.find(dt => dt.type === 'Denied Curriculum').id;
        this.deprecatedCurriculumTypeId = response.find(dt => dt.type === 'Deprecated Curriculum').id;
        this.licenseSnapshotType = response.find(dt => dt.type === this.licenseSnapshot).id;
      },
      () => this.loadingTrainingProgram = false
    );
  }

  updateContactsTable(): void {
    this.dataSource.data = this.trainingProgram.trainingProgramContacts;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  updateDocumentsTable(): void {
    this.documentSource.data = this.trainingProgram.trainingProgramDocuments;
    this.documentSource.sort = this.documentSort;
    this.documentSource.paginator = this.documentPaginator;
  }

  getProgramInformation(): void {
    this.programLocked = this.trainingProgram.status === this.sharedService.approved ||
      this.trainingProgram.status === this.sharedService.submitted ||
      this.trainingProgram.status === this.sharedService.renewalSubmitted ||
      this.trainingProgram.status === this.sharedService.resubmitted ||
      this.trainingProgram.status === this.sharedService.withdrawn;

    this.viewingDetails = !this.programLocked;
    this.programInfoForm.patchValue({
      programName: this.trainingProgram.programName,
      mailingIsHeadquarters: this.trainingProgram.mailingIsHeadquarters,
      facilityIsHeadquarters: this.trainingProgram.facilityIsHeadquarters,
      facilityIsMailing: this.trainingProgram.facilityIsMailing,
      headquartersStreet: this.trainingProgram.headquartersStreet,
      headquartersCity: this.trainingProgram.headquartersCity,
      headquartersState: this.trainingProgram.headquartersState,
      headquartersZip: this.trainingProgram.headquartersZip,
      headquartersCounty: this.trainingProgram.headquartersCounty,
      mailingStreet: this.trainingProgram.mailingStreet,
      mailingCity: this.trainingProgram.mailingCity,
      mailingState: this.trainingProgram.mailingState,
      mailingZip: this.trainingProgram.mailingZip,
      mailingCounty: this.trainingProgram.mailingCounty,
      facilityStreet: this.trainingProgram.facilityStreet,
      facilityCity: this.trainingProgram.facilityCity,
      facilityState: this.trainingProgram.facilityState,
      facilityZip: this.trainingProgram.facilityZip,
      facilityCounty: this.trainingProgram.facilityCounty,
      registeredIn: this.trainingProgram.registeredIn,
      website: this.trainingProgram.website,
      continuingEducation: this.trainingProgram.continuingEducation,
      trainingProgramInformationCorrect: this.trainingProgram.trainingProgramInformationCorrect,
      trainingProgramInformationCorrectReason: this.trainingProgram.trainingProgramInformationCorrectReason,
      secretaryofStateNumber: this.trainingProgram.secretaryofStateNumber
    });

    this.curriculumWorksheetForm.patchValue({
      curriculumWorksheetCorrect: this.trainingProgram.curriculumWorksheetCorrect
    });

    this.contactAttestationForm.patchValue({
      peopleCorrect: this.trainingProgram.peopleCorrect,
      peopleCorrectReason: this.trainingProgram.peopleCorrectReason
    });

    if (this.programLocked) {
      this.documentsForm.controls['curriculumHoursAssertion'].disable();
      this.documentsForm.controls['curriculumModulesAssertion'].disable();
      this.documentsForm.controls['curriculumPracticumAssertion'].disable();
      this.documentsForm.controls['curriculumAcceleratedAssertion'].disable();
      this.documentsForm.controls['curriculumNoticeAssertion'].disable();
      this.documentsForm.controls['curriculumCorrect'].disable();
      this.verifyForm.controls['responsibleAttestation'].disable();
      this.verifyForm.controls['informationAttestation'].disable();
      this.verifyForm.controls['publicRecordAttestation'].disable();
      this.verifyForm.controls['usAttestation'].disable();
      this.verifyForm.controls['recordKeepingAttestation'].disable();
      this.verifyForm.controls['programCompletionAttestation'].disable();
      this.verifyForm.controls['termAttestation'].disable();
      this.verifyForm.controls['paymentAssertion'].disable();
      this.verifyForm.controls['electronicSignature'].disable();
      this.contactAttestationForm.disable();
      this.programInfoForm.disable();
      this.curriculumWorksheetForm.disable();
    }
    else {
      this.documentsForm.controls['curriculumHoursAssertion'].enable();
      this.documentsForm.controls['curriculumModulesAssertion'].enable();
      this.documentsForm.controls['curriculumPracticumAssertion'].enable();
      this.documentsForm.controls['curriculumAcceleratedAssertion'].enable();
      this.documentsForm.controls['curriculumNoticeAssertion'].enable();
      this.documentsForm.controls['curriculumCorrect'].enable();
      this.verifyForm.controls['responsibleAttestation'].enable();
      this.verifyForm.controls['informationAttestation'].enable();
      this.verifyForm.controls['publicRecordAttestation'].enable();
      this.verifyForm.controls['usAttestation'].enable();
      this.verifyForm.controls['recordKeepingAttestation'].enable();
      this.verifyForm.controls['programCompletionAttestation'].enable();
      this.verifyForm.controls['termAttestation'].enable();
      this.verifyForm.controls['paymentAssertion'].enable();
      this.verifyForm.controls['electronicSignature'].enable();
    }
    this.documentsForm.patchValue({
      curriculumHoursAssertion: this.trainingProgram.curriculumAssertion,
      curriculumModulesAssertion: this.trainingProgram.curriculumAssertion,
      curriculumPracticumAssertion: this.trainingProgram.curriculumAssertion,
      curriculumAcceleratedAssertion: this.trainingProgram.curriculumAssertion,
      curriculumNoticeAssertion: this.trainingProgram.curriculumAssertion,
      curriculumCorrect: this.trainingProgram.curriculumCorrect
    });
    this.verifyForm.patchValue({
      responsibleAttestation: this.trainingProgram.confirmation,
      informationAttestation: this.trainingProgram.confirmation,
      publicRecordAttestation: this.trainingProgram.confirmation,
      usAttestation: this.trainingProgram.confirmation,
      recordKeepingAttestation: this.trainingProgram.confirmation,
      programCompletionAttestation: this.trainingProgram.confirmation,
      termAttestation: this.trainingProgram.confirmation,
      electronicSignature: this.trainingProgram.electronicSignature,
      paymentAssertion: this.trainingProgram.paymentAssertion
    });
    this.facilitatorForm.patchValue({
      facilitatorName: this.trainingProgram.facilitatorName,
      facilitatorLicense: this.trainingProgram.facilitatorLicense
    });

    this.updateContactsTable();
    this.updateDocumentsTable();
    this.trainingProgram.trainingProgramDocuments.forEach(document => {
      if (document.types[0] === this.curriculumTypeId) {
        this.curriculumId = document.id;
        this.activeDocuments.push(document);
        this.setCurriculumPageKey(document);
      }
      if (document.types[0] === this.curriculumUpdateTypeId) {
        this.hasCurriculumUpdate = true;
        this.activeDocuments.push(document);
      }
      this.curriculumComments[document.id] = document.comments;
    });
  }

  updateUserState(): void {
    if (this.trainingProgramSteps.includes(this.sharedService.userProfile.currentStep)) {
      this.step = this.stepsIndex[this.sharedService.userProfile.currentStep] !== 4 ? this.stepsIndex[this.sharedService.userProfile.currentStep] : 3;
    }
    if (this.sharedService.userProfile.currentPage !== this.page) {
      this.sharedService.userProfile.currentPage = this.page;
      this.sharedService.updateUserProfileState();
    }
    if (this.step === 2 || this.step === 6) {
      setTimeout(() => { this.updateContactsTable(); }, 500);
    }
  }

  toggleView(): void {
    if (this.showCloseWarning) {
      this.sharedService.openConfirm('Any changes made on this page will not be saved. Continue?');
      this.sharedService.confirmed().subscribe(confirmed => {
        if (confirmed) {
          this.viewingDetails = !this.viewingDetails;
          this.activeDocuments = [];
          this.curriculumPageKey = {};
        }
      });
    }
    else {
      this.activeDocuments = [];
      this.viewingDetails = !this.viewingDetails;
      this.curriculumPageKey = {};
    }
  }

  editApplication(program: ITrainingProgram): void {
    this.showEditLoading = true;
    this.step = 0;
    this.trainingProgram = program;
    this.programInfoForm.enable();
    this.getProgramInformation();
    setTimeout(() => { this.showEditLoading = false; }, 2000);
    setTimeout(() => { this.viewingDetails = true; }, 100);
  }

  filterTable(event: Event, table: string) {
    let value = (event.target as HTMLInputElement).value;
    let filter = value.trim().toLocaleLowerCase();
    if (table === 'contacts') {
      this.dataSource.filter = filter;
    }
    else {
      this.documentSource.filter = filter;
    }
  }

  mailingIsSame(): void {
    this.programInfoForm.get('mailingStreet').updateValueAndValidity();
    this.programInfoForm.get('mailingCity').updateValueAndValidity();
    this.programInfoForm.get('mailingState').updateValueAndValidity();
    this.programInfoForm.get('mailingZip').updateValueAndValidity();
  }

  facilityIsSame(): void {
    this.programInfoForm.get('facilityStreet').updateValueAndValidity();
    this.programInfoForm.get('facilityCity').updateValueAndValidity();
    this.programInfoForm.get('facilityState').updateValueAndValidity();
    this.programInfoForm.get('facilityZip').updateValueAndValidity();
  }

  //Program Information Custom Validators
  mailingStreetValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const street = control.value.mailingStreet;
      const same = control.value.mailingIsHeadquarters;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!same) {
        return (street !== null && street !== '' && street !== undefined) ? null : { mailingStreetRequired: true };
      }
      return null;
    };
  }

  validateMailingStreet(): boolean {
    if (this.programInfoForm.hasError('mailingStreetRequired') && this.programInfoForm.get('mailingStreet').touched) {
      this.programInfoForm.get('mailingStreet').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('mailingStreet').clearValidators();
    this.programInfoForm.get('mailingStreet').updateValueAndValidity();
    return false;
  }

  mailingCityValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const city = control.value.mailingCity;
      const same = control.value.mailingIsHeadquarters;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!same) {
        return (city !== null && city !== '' && city !== undefined) ? null : { mailingCityRequired: true };
      }
      return null;
    };
  }

  validateMailingCity(): boolean {
    if (this.programInfoForm.hasError('mailingCityRequired') && this.programInfoForm.get('mailingCity').touched) {
      this.programInfoForm.get('mailingCity').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('mailingCity').clearValidators();
    this.programInfoForm.get('mailingCity').updateValueAndValidity();
    return false;
  }

  mailingStateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const state = control.value.mailingState;
      const same = control.value.mailingIsHeadquarters;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!same) {
        return (state !== null && state !== '' && state !== undefined) ? null : { mailingStateRequired: true };
      }
      return null;
    };
  }

  validateMailingState(): boolean {
    if (this.programInfoForm.hasError('mailingStateRequired') && this.programInfoForm.get('mailingState').touched) {
      this.programInfoForm.get('mailingState').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('mailingState').clearValidators();
    this.programInfoForm.get('mailingState').updateValueAndValidity();
    return false;
  }

  mailingZipValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const zip = control.value.mailingZip;
      const same = control.value.mailingIsHeadquarters;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!same) {
        return (zip !== null && zip !== '' && zip !== undefined) ? null : { mailingZipRequired: true };
      }
      return null;
    };
  }

  validateMailingZip(): boolean {
    if (this.programInfoForm.hasError('mailingZipRequired') && this.programInfoForm.get('mailingZip').touched) {
      this.programInfoForm.get('mailingZip').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('mailingZip').clearValidators();
    this.programInfoForm.get('mailingZip').updateValueAndValidity();
    return false;
  }

  facilityStreetValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const street = control.value.facilityStreet;
      const sameAsHq = control.value.facilityIsHeadquarters;
      const sameAsMail = control.value.facilityIsMailing;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!sameAsHq && !sameAsMail) {
        return (street !== null && street !== '' && street !== undefined) ? null : { facilityStreetRequired: true };
      }
      return null;
    };
  }

  validateFacilityStreet(): boolean {
    if (this.programInfoForm.hasError('facilityStreetRequired') && this.programInfoForm.get('facilityStreet').touched) {
      this.programInfoForm.get('facilityStreet').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('facilityStreet').clearValidators();
    this.programInfoForm.get('facilityStreet').updateValueAndValidity();
    return false;
  }

  facilityCityValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const city = control.value.facilityCity;
      const sameAsHq = control.value.facilityIsHeadquarters;
      const sameAsMail = control.value.facilityIsMailing;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!sameAsHq && !sameAsMail) {
        return (city !== null && city !== '' && city !== undefined) ? null : { facilityCityRequired: true };
      }
      return null;
    };
  }

  validateFacilityCity(): boolean {
    if (this.programInfoForm.hasError('facilityCityRequired') && this.programInfoForm.get('facilityCity').touched) {
      this.programInfoForm.get('facilityCity').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('facilityCity').clearValidators();
    this.programInfoForm.get('facilityCity').updateValueAndValidity();
    return false;
  }

  facilityStateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const state = control.value.facilityState;
      const sameAsHq = control.value.facilityIsHeadquarters;
      const sameAsMail = control.value.facilityIsMailing;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!sameAsHq && !sameAsMail) {
        return (state !== null && state !== '' && state !== undefined) ? null : { facilityStateRequired: true };
      }
      return null;
    };
  }

  validateFacilityState(): boolean {
    if (this.programInfoForm.hasError('facilityStateRequired') && this.programInfoForm.get('facilityState').touched) {
      this.programInfoForm.get('facilityState').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('facilityState').clearValidators();
    this.programInfoForm.get('facilityState').updateValueAndValidity();
    return false;
  }

  facilityZipValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const zip = control.value.facilityZip;
      const sameAsHq = control.value.facilityIsHeadquarters;
      const sameAsMail = control.value.facilityIsMailing;
      if (this.trainingProgram.isRenewal) {
        return null;
      }
      if (!sameAsHq && !sameAsMail) {
        return (zip !== null && zip !== '' && zip !== undefined) ? null : { facilityZipRequired: true };
      }
      return null;
    };
  }

  validateFacilityZip(): boolean {
    if (this.programInfoForm.hasError('facilityZipRequired') && this.programInfoForm.get('facilityZip').touched) {
      this.programInfoForm.get('facilityZip').setErrors(['required']);
      return true;
    }
    this.programInfoForm.get('facilityZip').clearValidators();
    this.programInfoForm.get('facilityZip').updateValueAndValidity();
    return false;
  }

  facilitatorValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const facilitatorName = control.value.facilitatorName;
      const facilitatorLicense = control.value.facilitatorLicense;

      const today = new Date();
      const cutoffDate = new Date('2025-09-01');

      if (today >= cutoffDate) {
        if ((facilitatorName === null || facilitatorName === '') && (facilitatorLicense === null || facilitatorLicense === ''))
          return { facilitatorNameRequired: true, facilitatorLicenseRequired: true };
        if (facilitatorName === null || facilitatorName === '')
          return { facilitatorNameRequired: true };
        if (facilitatorLicense === null || facilitatorLicense === '')
          return { facilitatorLicenseRequired: true };
      }
      return null;
    }
  }

  validateFacilitatorName(): boolean {
    if (this.facilitatorForm.hasError('facilitatorNameRequired') && this.facilitatorForm.get('facilitatorName').touched) {
      this.facilitatorForm.get('facilitatorName').setErrors({ required: true });
      return true;
    }
    this.facilitatorForm.get('facilitatorName').clearValidators();
    this.facilitatorForm.get('facilitatorName').updateValueAndValidity();
    return false;
  }

  validateFacilitatorLicense(): boolean {
    if (this.facilitatorForm.hasError('facilitatorLicenseRequired') && this.facilitatorForm.get('facilitatorLicense').touched) {
      this.facilitatorForm.get('facilitatorLicense').setErrors({ required: true });
      return true;
    }
    return false;
  }

  showFiveYear(applicationDate: string): boolean {
    const comparisonDate = new Date(2025, 0, 2);
    const parsedDate = new Date(applicationDate);

    if (applicationDate && parsedDate >= comparisonDate) {
      return false;
    }

    if (!applicationDate) {
      return false;
    }
    return parsedDate < comparisonDate;
  }
  //End of Validators

  showNewIncompleteDates(applicationDate: string): boolean {
    const jan1st2025 = new Date('2025-01-01');
    if (!applicationDate) {
      return true;
    }
    const parsedDate = new Date(applicationDate);
    return parsedDate > jan1st2025;
  }

  addNewContact(): void {
    let contact: ITrainingProgramContact = {
      id: 0,
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      phone: '',
      attestation: false,
      isDirector: false,
      isLeadEducator: false,
      isInstructor: false,
      isResponsible: false,
      isBusinessAgent: false,
      trainingProgramId: this.trainingProgram.id,
      dateCreated: '',
      archived: false
    };

    const dialogRef = this.dialog.open(ProgramContactDialog, {
      data: contact,
      role: 'dialog',
      ariaLabel: "Add New Contact",
      minWidth: !this.sharedService.mobile ? '400px' : '300px',
      maxWidth: !this.sharedService.mobile ? '700px' : '300px',
      panelClass: this.sharedService.userProfile.theme === "dark" ? "theme-dark" : "",
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (!this.isCancelled(response)) {
        this.trainingProgram.trainingProgramContacts.push(response);
        this.updateContactsTable();
        this.checkContactsValid();
      }
    });
  }

  editContact(contact: ITrainingProgramContact): void {
    const dialogRef = this.dialog.open(ProgramContactDialog, {
      data: contact,
      id: "editContact",
      role: "dialog",
      ariaLabel: "Edit " + contact.firstName + " " + contact.lastName,
      closeOnNavigation: false,
      minWidth: !this.sharedService.mobile ? '400px' : '',
      maxWidth: !this.sharedService.mobile ? '700px' : '300px',
      panelClass: this.sharedService.userProfile.theme === "dark" ? "theme-dark" : ""
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (!this.isCancelled(response)) {
        var contactIndex = this.trainingProgram.trainingProgramContacts.findIndex(tpc => tpc.id === response.id);
        this.trainingProgram.trainingProgramContacts[contactIndex] = response;
        this.dataSource.data = this.trainingProgram.trainingProgramContacts;
        this.checkContactsValid();
      }
    });
  }

  deleteContact(contact: ITrainingProgramContact): void {
    this.sharedService.openConfirm('Delete ' + contact.firstName + ' ' + contact.lastName + '?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.trainingProgramService.deleteTrainingProgramContact(contact.id).subscribe(
          () => {
            this.trainingProgram.trainingProgramContacts = this.trainingProgram.trainingProgramContacts.filter(tpc => tpc.id !== contact.id);
            this.updateContactsTable();
            this.checkContactsValid();
          },
          error => {
            this.sharedService.consoleLog(error);
          }
        );
      }
    });
  }

  private getDocumentUploadForkJoin(files: FileList, parentId: number, types: number[]): Observable<ITrainingProgramDocument[]> {
    let tasks = Array.from(files).map(dirtyFile => {
      let file = new File([dirtyFile], dirtyFile.name.replace(/[^A-Za-z0-9.]/g, ''));
      if (this.sharedService.validateFile(file)) {
        let upload: IDocument = this.getBlankDocument();
        upload.name = file.name;
        upload.parentId = parentId;
        upload.types = types;
        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("document", JSON.stringify(upload));
        return this.trainingProgramService.uploadTrainingProgramDocument(formData);
      }
      else
        this.toastr.error("Unsupported File Type")
    });
    return forkJoin(tasks);
  }

  private getBlankDocument(): IDocument {
    return {
      id: 0,
      name: "",
      comments: "",
      extenstion: "",
      dateCreated: "",
      dateLastUpdated: "",
      createdBy: "",
      lastUpdatedBy: "",
      parentId: 0,
      types: [],
      adminOnly: false,
      deprecated: false
    }
  }

  checkContactsValid(): void {
    let director: boolean = this.trainingProgram.trainingProgramContacts.some(tpc => tpc.isDirector);
    let responsible: boolean = this.trainingProgram.trainingProgramContacts.some(tpc => tpc.isResponsible);
    let businessAgent: boolean = this.trainingProgram.trainingProgramContacts.some(tpc => tpc.isBusinessAgent);
    let leadEducator: boolean = this.trainingProgram.trainingProgramContacts.filter(tpc => tpc.isLeadEducator).length > 1;
    if (responsible && businessAgent && director && leadEducator) {
      this.contactsValid = true;
    }
    else {
      this.contactsValid = false;
    }
  }

  uploadCurriculum(event: Event): void {
    this.uploading = true;
    let files: FileList = (event.target as HTMLInputElement).files;
    let type: number[] = [];
    let typeId: number = !this.programLocked ? this.curriculumTypeId : this.curriculumUpdateTypeId;
    type.push(typeId);
    this.getDocumentUploadForkJoin(files, this.trainingProgram.id, type).subscribe(
      response => {
        response.forEach(document => {
          if (document.types.includes(this.curriculumTypeId)) {
            this.curriculumId = document.id;
          }
          this.trainingProgram.trainingProgramDocuments.push(document);
          this.curriculumComments[document.id] = "";
          this.uploading = false;
        });
      },
      error => {
        this.uploading = false;
        this.sharedService.consoleLog(error);
        (event.target as HTMLInputElement).value = '';
      },
      () => {
        this.updateDocumentsTable();
        this.checkCurriculumValid();
        (event.target as HTMLInputElement).value = '';
      }
    );
  }

  addCurriculumComment(id: number, event: Event): void {
    let comment = (event.target as HTMLTextAreaElement).value;
    this.curriculumComments[id] = comment;
  }

  downloadCurriculum(document: IDocument): void {
    this.downloading = true;
    this.trainingProgramService.downloadTrainingProgramFile(document.id).subscribe(
      (response) => this.saveFile(document.name, response),
      (error) => {
        this.downloading = false;
        this.sharedService.consoleLog(error);
      },
      () => this.downloading = false
    );
  }

  deleteCurriculum(document: IDocument): void {
    this.sharedService.openConfirm('Delete ' + document.name + '?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.deleting = true;
        this.trainingProgramService.deleteTrainingProgramDocument(document.id).subscribe(
          () => {
            this.trainingProgram.trainingProgramDocuments = this.trainingProgram.trainingProgramDocuments.filter(tpd => tpd.id !== document.id);
            this.checkCurriculumValid();
            this.deleting = false;
            this.updateDocumentsTable();
          },
          error => {
            this.deleting = false;
            this.sharedService.consoleLog(error);
          }
        );
      }
    });
  }

  openCurriculumUpdateDialog(): void {
    var dialogObject: ICurriculumUpdateDialog = {
      curriculumUpdateTypeId: this.curriculumUpdateTypeId,
      id: this.trainingProgram.id
    }
    const dialogRef = this.dialog.open(CurriculumUpdateDialog, {
      data: dialogObject,
      role: 'dialog',
      ariaLabel: "Update Curriculum Dialog",
      minWidth: !this.sharedService.mobile ? '500px' : '300px',
      maxWidth: !this.sharedService.mobile ? '700px' : '300px',
      panelClass: this.sharedService.userProfile.theme === "dark" ? "theme-dark" : "",
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (!this.isCancelled(response)) {
        let updatedCurriculum: ITrainingProgramDocument = response;
        this.hasCurriculumUpdate = true;
        this.trainingProgram.trainingProgramDocuments.push(updatedCurriculum);
        this.curriculumComments[updatedCurriculum.id] = updatedCurriculum.comments;
        this.activeDocuments.push(updatedCurriculum);
        this.updateDocumentsTable();
      }
    });
  }

  checkCurriculumValid(): void {
    this.curriculumValid = this.trainingProgram.trainingProgramDocuments.length > 0;
  }

  curriculumWorksheetComplete(): void {
    this.worksheetComplete = true;
    let curriculum = this.trainingProgram.trainingProgramDocuments.find(tpd => tpd.types.includes(this.curriculumTypeId));
    if (curriculum != null) {
      this.referenceIds.every(id => {
        if (this.curriculumPageKey[id] === '' || this.curriculumPageKey[id] === null) {
          this.worksheetComplete = false;
          return false;
        }
      });
    }
  }

  selectCurriculumToReference(event: MatSelectChange): void {
    this.curriculumId = event.value;
    let curriculum = this.trainingProgram.trainingProgramDocuments.find(tpd => tpd.id === this.curriculumId);
    this.setCurriculumPageKey(curriculum);
  }

  setCurriculumPageKey(document: ITrainingProgramDocument): void {
    this.curriculumPageKey = {};
    document.curriculumPageReferences.forEach(reference => {
      this.curriculumPageKey[reference.referenceId] = reference.pages;
    });
  }

  getCurriculumMap(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.trainingProgramService.getCurriculumMap().subscribe(
        response => this.curriculumMap = response,
        error => {
          this.sharedService.consoleLog(error);
          reject();
        },
        () => {
          this.curriculumMap.forEach(element => {
            element.subSections.forEach(subSection => {
              this.curriculumPageKey[subSection.referenceId] = '';
              this.referenceIds.push(subSection.referenceId);
              subSection.subSections.forEach(secondSection => {
                this.curriculumPageKey[secondSection.referenceId] = '';
                this.referenceIds.push(secondSection.referenceId);
              });
            });
          });
          resolve();
        }
      );
    });
  }

  uploadCurriculumPageReference(): void {
    if (this.trainingProgram.trainingProgramDocuments.length > 0) {
      let curriculum: ITrainingProgramDocument = this.trainingProgram.trainingProgramDocuments.find(tpd => tpd.id == this.curriculumId);
      curriculum.curriculumPageReferences.forEach(reference => {
        reference.pages = this.curriculumPageKey[reference.referenceId];
      });

      let currentReferenceIds: string[] = curriculum.curriculumPageReferences.map(ref => ref.referenceId);

      this.referenceIds.forEach(ref => {
        if (!currentReferenceIds.includes(ref)) {
          let newReference: ICurriculumPageReference =
          {
            id: 0,
            referenceId: ref,
            pages: this.curriculumPageKey[ref],
            documentId: curriculum.id
          };
          curriculum.curriculumPageReferences.push(newReference);
        }
      });

      this.trainingProgramService.uploadCurriculumPageReference(curriculum.curriculumPageReferences, this.trainingProgram.id).subscribe(
        response => curriculum.curriculumPageReferences = response,
        error => this.sharedService.consoleLog(error),
        () => this.curriculumWorksheetComplete()
      );
    }
  }

  saveFile(fileName: string, blob: Blob) {
    let file = URL.createObjectURL(blob);
    var fileDownload = document.createElement("a");
    fileDownload.href = file;
    fileDownload.download = fileName;
    fileDownload.click();
  }

  displaySavingNotice(): void {
    this.saving = true;
    setTimeout(() => { this.saving = false }, 1500);
  }

  changeStep(step: StepperSelectionEvent): void {
    this.sharedService.userProfile.currentStep = this.trainingProgramSteps[step.selectedIndex];
    this.sharedService.updateUserProfileState();
    let warningSteps: number[] = [1, 3, 4]
    this.showCloseWarning = warningSteps.includes(step.selectedIndex);
    if (step.previouslySelectedIndex === 1) {
      this.programInfoForm.markAllAsTouched();
      this.updateProgramInformation();
      this.validateTrainingProgramInformationCorrect();
    }
    if (step.selectedIndex === 2) {
      this.updateContactsTable();
    }
    if (step.previouslySelectedIndex === 2) {
      this.displaySavingNotice();
      this.checkContactsValid();
      this.facilitatorForm.markAllAsTouched();
      this.validateFacilitatorName();
      this.validateFacilitatorLicense();
      this.updateProgramInformation();
      this.validatePeopleCorrect();
    }
    if (step.selectedIndex === 3) {
      this.updateDocumentsTable();
      this.validateCurriculumCorrect();
    }
    if (step.previouslySelectedIndex === 3) {
      this.updateCurriculumInformation();
    }
    if (step.previouslySelectedIndex === 4) {
      this.displaySavingNotice();
      this.uploadCurriculumPageReference();
      this.validateCurriculumWorksheetCorrect();
      this.updateProgramInformation();
    }
    if (step.selectedIndex === 6) {
      this.programInfoForm.markAsDirty();
      this.facilitatorForm.markAllAsTouched();
      this.validateFacilitatorName();
      this.validateFacilitatorLicense();
      this.updateContactsTable();
      this.checkContactsValid();
      this.checkCurriculumValid();
      this.curriculumWorksheetComplete();
      this.validateTrainingProgramInformationCorrect();
      this.validatePeopleCorrect();
      this.validateCurriculumCorrect();
      this.validateCurriculumWorksheetCorrect();
    }
  }

  updateProgramInformation(): void {
    this.displaySavingNotice();

    let facilitatorInformation = this.facilitatorForm.value;
    this.trainingProgram.facilitatorName = facilitatorInformation.facilitatorName;
    this.trainingProgram.facilitatorLicense = facilitatorInformation.facilitatorLicense;

    let program = this.programInfoForm.getRawValue();
    let peopleForm = this.contactAttestationForm.getRawValue();
    let curriculumWorksheetForm = this.curriculumWorksheetForm.getRawValue();
    this.trainingProgram.programName = program.programName;
    this.trainingProgram.website = program.website;
    this.trainingProgram.registeredIn = program.registeredIn;
    this.trainingProgram.continuingEducation = program.continuingEducation;
    this.trainingProgram.mailingIsHeadquarters = program.mailingIsHeadquarters;
    this.trainingProgram.facilityIsHeadquarters = program.facilityIsHeadquarters;
    this.trainingProgram.facilityIsMailing = program.facilityIsMailing;
    this.trainingProgram.headquartersStreet = program.headquartersStreet;
    this.trainingProgram.headquartersCity = program.headquartersCity;
    this.trainingProgram.headquartersState = program.headquartersState;
    this.trainingProgram.headquartersZip = program.headquartersZip;
    this.trainingProgram.headquartersCounty = program.headquartersCounty;
    this.trainingProgram.secretaryofStateNumber = program.secretaryofStateNumber;
    if (program.mailingIsHeadquarters) {
      this.trainingProgram.mailingStreet = program.headquartersStreet;
      this.trainingProgram.mailingCity = program.headquartersCity;
      this.trainingProgram.mailingState = program.headquartersState;
      this.trainingProgram.mailingZip = program.headquartersZip;
      this.trainingProgram.mailingCounty = program.headquartersZip;
    }
    else {
      this.trainingProgram.mailingStreet = program.mailingStreet;
      this.trainingProgram.mailingCity = program.mailingCity;
      this.trainingProgram.mailingState = program.mailingState;
      this.trainingProgram.mailingZip = program.mailingZip;
      this.trainingProgram.mailingCounty = program.mailingCounty;
    }
    if (program.facilityIsHeadquarters) {
      this.trainingProgram.facilityStreet = program.headquartersStreet;
      this.trainingProgram.facilityCity = program.headquartersCity;
      this.trainingProgram.facilityState = program.headquartersState;
      this.trainingProgram.facilityZip = program.headquartersZip;
      this.trainingProgram.facilityCounty = program.headquartersZip;
    }
    else if (program.facilityIsMailing) {
      this.trainingProgram.facilityStreet = program.mailingStreet;
      this.trainingProgram.facilityCity = program.mailingCity;
      this.trainingProgram.facilityState = program.mailingState;
      this.trainingProgram.facilityZip = program.mailingZip;
      this.trainingProgram.facilityCounty = program.mailingCounty;
    }
    else {
      this.trainingProgram.facilityStreet = program.facilityStreet;
      this.trainingProgram.facilityCity = program.facilityCity;
      this.trainingProgram.facilityState = program.facilityState;
      this.trainingProgram.facilityZip = program.facilityZip;
      this.trainingProgram.facilityCounty = program.facilityCounty;
    }
    if (this.trainingProgram.isRenewal) {
      this.trainingProgram.trainingProgramInformationCorrect = program.trainingProgramInformationCorrect;
      this.trainingProgram.trainingProgramInformationCorrectReason = program.trainingProgramInformationCorrectReason;
      this.trainingProgram.peopleCorrect = peopleForm.peopleCorrect;
      this.trainingProgram.peopleCorrectReason = peopleForm.peopleCorrectReason;
      this.trainingProgram.curriculumWorksheetCorrect = curriculumWorksheetForm.curriculumWorksheetCorrect;
    }
    this.trainingProgramService.updateTrainingProgram(this.trainingProgram);
    this.programInfoForm.markAsDirty();
  }

  updateCurriculumInformation(): void {
    this.displaySavingNotice();
    this.checkCurriculumValid();
    if (!this.programLocked) {
      let curriculumInfo = this.documentsForm.value;
      this.documentsForm.markAsDirty();
      this.trainingProgram.curriculumAssertion = curriculumInfo.curriculumHoursAssertion &&
        curriculumInfo.curriculumModulesAssertion &&
        curriculumInfo.curriculumPracticumAssertion &&
        curriculumInfo.curriculumAcceleratedAssertion &&
        curriculumInfo.curriculumNoticeAssertion;
      this.trainingProgram.curriculumCorrect = curriculumInfo.curriculumCorrect;
      this.trainingProgramService.updateTrainingProgram(this.trainingProgram);
    }
    this.trainingProgram.trainingProgramDocuments.forEach(document => {
      document.comments = this.curriculumComments[document.id];
    });
    this.trainingProgramService.uploadTrainingProgramDocumentComments(this.trainingProgram.trainingProgramDocuments).subscribe(
      () => {
        return;
      },
      error => this.sharedService.consoleLog(error)
    );
  }

  applicationValid(): boolean {
    return this.programInfoForm.valid &&
      this.contactsValid &&
      this.curriculumValid &&
      this.verifyForm.valid &&
      this.documentsForm.valid &&
      this.worksheetComplete &&
      this.facilitatorForm.valid &&
      this.contactAttestationForm.valid &&
      this.curriculumWorksheetForm.valid;
  }

  submitProgram(): void {
    let programInfo = this.programInfoForm.getRawValue();
    let curriculumInfo = this.documentsForm.getRawValue();
    let assertions = this.verifyForm.getRawValue();
    this.trainingProgram.programName = programInfo.programName;
    this.trainingProgram.mailingIsHeadquarters = programInfo.mailingIsHeadquarters;
    this.trainingProgram.facilityIsHeadquarters = programInfo.facilityIsHeadquarters;
    this.trainingProgram.facilityIsMailing = programInfo.facilityIsMailing;
    this.trainingProgram.headquartersStreet = programInfo.headquartersStreet;
    this.trainingProgram.headquartersCity = programInfo.headquartersCity;
    this.trainingProgram.headquartersState = programInfo.headquartersState;
    this.trainingProgram.headquartersZip = programInfo.headquartersZip;
    this.trainingProgram.headquartersCounty = programInfo.headquartersCounty;
    this.trainingProgram.mailingStreet = programInfo.mailingStreet;
    this.trainingProgram.mailingCity = programInfo.mailingCity;
    this.trainingProgram.mailingState = programInfo.mailingState;
    this.trainingProgram.mailingZip = programInfo.mailingZip;
    this.trainingProgram.mailingCounty = programInfo.mailingCounty;
    this.trainingProgram.facilityStreet = programInfo.facilityStreet;
    this.trainingProgram.facilityCity = programInfo.facilityCity;
    this.trainingProgram.facilityState = programInfo.facilityState;
    this.trainingProgram.facilityZip = programInfo.facilityZip;
    this.trainingProgram.facilityCounty = programInfo.facilityCounty;
    this.trainingProgram.registeredIn = programInfo.registeredIn;
    this.trainingProgram.secretaryofStateNumber = programInfo.secretaryofStateNumber;
    this.trainingProgram.website = programInfo.website;
    this.trainingProgram.continuingEducation = programInfo.continuingEducation;
    this.trainingProgram.confirmation = assertions.responsibleAttestation &&
      assertions.informationAttestation &&
      assertions.publicRecordAttestation &&
      assertions.usAttestation &&
      assertions.recordKeepingAttestation &&
      assertions.programCompletionAttestation &&
      assertions.termAttestation;
    this.trainingProgram.curriculumAssertion = curriculumInfo.curriculumAssertion;
    this.trainingProgram.paymentAssertion = assertions.paymentAssertion;
    this.trainingProgram.electronicSignature = assertions.electronicSignature;
    this.trainingProgramService.submitTrainingProgram(this.trainingProgram).subscribe(
      response => this.trainingPrograms = response,
      error => this.sharedService.consoleLog(error),
      async () => {
        await this.saveSnapshotAndUpload();
        this.sharedService.userProfile.currentStep = '0';
        this.updateUserState();
        this.router.navigate(['/training/confirmation/' + this.trainingProgram.id]);
        this.toggleView();
      }
    );
  }

  saveSnapshotAndUpload(): void {
     this.trainingProgram.applicationDate = this.trainingProgram.applicationDate || moment().format('MM/DD/YYYY hh:mm A');

    const dialogRef = this.dialog.open(TrainingProgramSnapshotDialog, {
      data: {
            curriculumPageKey: this.curriculumPageKey,
            trainingProgram: this.trainingProgram,
            curriculumMap: this.curriculumMap
      },
      width: '1000px',
      maxWidth: '1000px',
      height: 'auto',
      position: { top: '-9999px', left: '-9999px' },
      autoFocus: false,
      panelClass: 'hidden-dialog'
    });

    dialogRef.afterOpened().subscribe(async () => {
      const dialogContent = document.querySelector('#snapshotPdf') as HTMLElement;

      const snapshotPdf = dialogContent.querySelector('#snapshotPdf') as HTMLElement;
      if (snapshotPdf) {
        snapshotPdf.style.paddingBottom = '16px';
      }

      const drawOptions = {
        scale: 0.75,
        paperSize: 'A4',
        forcePageBreak: '.page-break',
        margin: { top: '0.5cm', left: '0.5cm', right: '0.5cm', bottom: '2.5cm' },
        template: (data) => `
          <div style="position: absolute; bottom: 0; left: 0; right: 0; text-align: center; font-size: 12px; padding: 8px;">
            Page ${data.pageNum} of ${data.totalPages} | License type: Training Program | Program Id: ${this.trainingProgram.programId} <br>| Date of application submission: ${this.trainingProgram.applicationDate} | Renewal number: ${this.trainingProgram.renewalCount}
          </div>
        `
      };

      const group = await drawDOM(dialogContent, drawOptions);

      const pdfDataUri: string = await exportPDF(group, drawOptions);

      const pdfBlob = this.dataUriToBlob(pdfDataUri);

      const filename = `TrainingProgramSnapshot-${this.trainingProgram.programId}.pdf`;
      const file = new File([pdfBlob], filename, { type: 'application/pdf' });

      const upload: IDocument = {
        id: 0,
        name: file.name,
        comments: '',
        extenstion: 'pdf',
        dateCreated: '',
        dateLastUpdated: '',
        createdBy: '',
        lastUpdatedBy: '',
        parentId: this.trainingProgram.id,
        types: [this.licenseSnapshotType],
        adminOnly: true,
        deprecated: false
      };

      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('document', JSON.stringify(upload));

      this.trainingProgramService.uploadTrainingProgramDocument(formData).subscribe({
        next: (response) => {
        },
        error: (err) => {
          console.error('Upload failed', err);
        },
        complete: () => {
          dialogRef.close();
        }
      });
    });
  }

  private dataUriToBlob(dataUri: string): Blob {
    const byteString = atob(dataUri.split(',')[1]);
    const mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  hasRenewal(license: ITrainingProgram): boolean {
    if (this.trainingPrograms.find(l => (l.isRenewal && l.programId == license.programId && (l.status != this.sharedService.approved && l.status != this.sharedService.resubmitted && l.status != this.sharedService.incomplete && l.status != this.sharedService.insufficient
      && l.status != this.sharedService.denied && l.status != this.sharedService.revoked && l.status != this.sharedService.expired && l.status != this.sharedService.withdrawn
      && l.status != this.sharedService.surrendered && l.status != this.sharedService.closed))) != null)
      return true;
    return false;
  }

  createRenewal(license: ITrainingProgram): void {
    this.sharedService.openConfirm("Are you sure you want to start a renewal application?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.startedRenewal = true;
          this.showEditLoading = true;
          this.trainingProgramService.createRenewalLicense(license.id).subscribe(
            response => {
              this.trainingPrograms.push(response);
              this.showEditLoading = false;
              this.viewRenewal(response);
            },
            error => {
              console.log('error', error)
            });
        }
      }
    );
  }

  contactIsAfterOrEqual(contactDate: string, programDate: string): boolean {
    const contactDateFormatted = new Date(contactDate);
    const programDateFormatted = new Date(programDate);
    return contactDateFormatted >= programDateFormatted;
  }

  viewRenewal(license: ITrainingProgram): void {
    this.showEditLoading = true;
    this.step = 0;
    this.trainingProgram = license;
    this.getProgramInformation();
    this.programInfoForm.disable();
    if (!this.programLocked) {
      this.contactAttestationForm.enable();
      this.programInfoForm.controls['website'].enable();
      this.programInfoForm.controls['secretaryofStateNumber'].enable();
      this.programInfoForm.controls['continuingEducation'].enable();
      this.programInfoForm.controls['trainingProgramInformationCorrect'].enable();
      this.programInfoForm.controls['trainingProgramInformationCorrectReason'].enable();
      this.curriculumWorksheetForm.enable();
    }

    setTimeout(() => {
      this.showEditLoading = false;
      this.renewing = true;
      this.viewingDetails = true;
    }, 100);
  }

  startNewApplication(): void {
    this.sharedService.openConfirm('Start a new application?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.trainingProgram = null;
        this.sharedService.userProfile.currentStep = '0';
        this.trainingProgramService.createTrainingProgram().subscribe(
          response => {
            this.trainingPrograms.push(response);
            this.trainingProgram = response;
            this.curriculumPageKey = {};
            this.getProgramInformation();
          },
          error => this.sharedService.consoleLog(error),
          () => this.viewingDetails = true
        );
      }
    });
  }

  viewRevocation(): void {
    let revocation: ITrainingProgramRevocation = this.trainingPrograms.find(tps => tps.status === this.sharedService.approved)
      .trainingProgramRevocations.find(tpr => tpr.active);
    this.dialog.open(TrainingProgramRevocationDialog, {
      data: revocation,
      role: 'dialog',
      ariaLabel: 'Revocation Information Dialog',
      minWidth: !this.sharedService.mobile ? '400px' : '300px',
      maxWidth: !this.sharedService.mobile ? '800px' : '300px',
      maxHeight: !this.sharedService.mobile ? '1000px' : '500px',
      panelClass: this.sharedService.darkMode ? "theme-dark" : ""
    });
  }

  requestWithdraw(license: ITrainingProgram): void {
    this.sharedService.openConfirm("Do you want to request to withdraw your application? To withdraw, you need to send OPS the \"Request to Withdraw Application\" form. It is found on the \"TLC Home\" page.");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.trainingProgramService.requestWithdraw(license.id).subscribe(
            response => {
              let index = this.trainingPrograms.findIndex(fl => fl.id == license.id);
              this.trainingPrograms[index] = response;
            },
            error => console.log('error', error),
          )
        }
      });
  }

  requestSurrender(license: ITrainingProgram): void {
    this.sharedService.openConfirm("Do you want to request to surrender your application? To surrender, you need to send OPS the \"License Surrender Request\" form. It is found on the \"TLC Home\" page.");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.trainingProgramService.requestSurrender(license.id).subscribe(
            response => {
              let index = this.trainingPrograms.findIndex(fl => fl.id == license.id);
              this.trainingPrograms[index] = response;
            },
            error => console.log('error', error),
          )
        }
      });
  }

  isCancelled(response: any): boolean {
    return response === 'cancel' || response === null || response === '' || response === undefined;
  }

  viewApprovalSummary(program: ITrainingProgram) {
    this.dialog.open(PrintTrainingProgramComponent, {
      data: program,
      role: 'dialog',
      ariaLabel: 'Approved Curriculum Certificate',
      maxWidth: !this.sharedService.mobile ? 'min(900px, 95vw)' : 'min(800px, 95vw)',
      maxHeight: this.sharedService.mobile ? '750px' : '900px',
    });
  }

  validateTrainingProgramInformationCorrect(): boolean {

    if (this.programInfoForm.hasError('trainingProgramInformationCorrectRequired') && !this.programInfoForm.get('trainingProgramInformationCorrect').value) {
      this.programInfoForm.get('trainingProgramInformationCorrect').setErrors(['required']);
      return true;

    }
    this.programInfoForm.get('trainingProgramInformationCorrect').clearValidators();
    this.programInfoForm.get('trainingProgramInformationCorrect').updateValueAndValidity();
    return false;
  }

  trainingProgramInformationCorrectValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const pageCorrect = control.value.trainingProgramInformationCorrect;
      if (!pageCorrect && this.trainingProgram.isRenewal) {
        return { trainingProgramInformationCorrectRequired: true };
      }
      return null;
    };
  }

  validatePeopleCorrect(): boolean {
    if (this.contactAttestationForm.hasError('peopleCorrectRequired') && !this.contactAttestationForm.get('peopleCorrect').value) {
      this.contactAttestationForm.get('peopleCorrect').setErrors(['required']);
      return true;
    }
    this.contactAttestationForm.get('peopleCorrect').clearValidators();
    this.contactAttestationForm.get('peopleCorrect').updateValueAndValidity();
    return false;
  }

  peopleCorrectValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const pageCorrect = control.value.peopleCorrect;
      if (!pageCorrect && this.trainingProgram.isRenewal) {
        return { peopleCorrectRequired: true };
      }
      return null;
    };
  }

  validateCurriculumCorrect(): boolean {
    if (this.documentsForm.hasError('curriculumCorrectRequired') && !this.documentsForm.get('curriculumCorrect').value) {
      this.documentsForm.get('curriculumCorrect').setErrors(['required']);
      return true;
    }
    this.documentsForm.get('curriculumCorrect').clearValidators();
    this.documentsForm.get('curriculumCorrect').updateValueAndValidity();
    return false;
  }

  curriculumCorrectValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const curriculumCorrect = control.value.curriculumCorrect;
      if (!curriculumCorrect && this.trainingProgram.isRenewal) {
        return { curriculumCorrectRequired: true };
      }
      return null;
    };
  }

  validateCurriculumWorksheetCorrect(): boolean {
    if (this.curriculumWorksheetForm.hasError('curriculumWorksheetCorrectRequired') && !this.curriculumWorksheetForm.get('curriculumWorksheetCorrect').value) {
      this.curriculumWorksheetForm.get('curriculumWorksheetCorrect').setErrors(['required']);
      return true;
    }
    this.curriculumWorksheetForm.get('curriculumWorksheetCorrect').clearValidators();
    this.curriculumWorksheetForm.get('curriculumWorksheetCorrect').updateValueAndValidity();
    return false;
  }

  curriculumWorksheetCorrectValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const curriculumCorrect = control.value.curriculumWorksheetCorrect;
      if (!curriculumCorrect && this.trainingProgram.isRenewal) {
        return { curriculumWorksheetCorrectRequired: true };
      }
      return null;
    };
  }
}

@Component({
    selector: 'program-contact-dialog',
    templateUrl: 'dialog-program-contact.html',
    styleUrls: ['./training-program.component.scss'],
    standalone: false
})

export class ProgramContactDialog implements OnInit {
  public processing: boolean = false;
  public contactsForm = new UntypedFormGroup({
    firstName: new UntypedFormControl("", [Validators.required]),
    lastName: new UntypedFormControl("", [Validators.required]),
    title: new UntypedFormControl(""),
    directorAttestation: new UntypedFormControl(false),
    instructorAttestation: new UntypedFormControl(false),
    leadAttestation: new UntypedFormControl(false),
    isDirector: new UntypedFormControl(false),
    isResponsible: new UntypedFormControl(false),
    isBusinessAgent: new UntypedFormControl(false),
    isLeadEducator: new UntypedFormControl(false),
    isInstructor: new UntypedFormControl(false),
    email: new UntypedFormControl("", [Validators.email]),
    phone: new UntypedFormControl("", [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)])
  }, {
    validators: [
      this.directorAttestationValidator(),
      this.instructorAttestationValidator(),
      this.leadAttestationValidator(),
      this.phoneRequired(),
      this.emailRequired()
    ]
  });

  constructor(public dialogRef: MatDialogRef<ProgramContactDialog>,
    private trainingProgramService: TrainingProgramService,
    public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public contact: ITrainingProgramContact) { }

  ngOnInit(): void {
    this.contactsForm.patchValue({
      firstName: this.contact.firstName,
      lastName: this.contact.lastName,
      title: this.contact.title,
      isDirector: this.contact.isDirector,
      isResponsible: this.contact.isResponsible,
      isBusinessAgent: this.contact.isBusinessAgent,
      isLeadEducator: this.contact.isLeadEducator,
      isInstructor: this.contact.isInstructor,
      directorAttestation: this.contact.isDirector ? this.contact.attestation : false,
      instructorAttestation: this.contact.isInstructor ? this.contact.attestation : false,
      leadAttestation: this.contact.isLeadEducator ? this.contact.attestation : false,
      email: this.contact.email,
      phone: this.contact.phone
    });
  }

  displayPhoneAndEmail(): boolean {
    let formValues = this.contactsForm.value;
    return (formValues.isDirector || formValues.isResponsible || formValues.isBusinessAgent || formValues.isLeadEducator);
  }

  phoneRequired(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const director = control.value.isDirector;
      const responsible = control.value.isResponsible;
      const ba = control.value.isBusinessAgent;
      const lead = control.value.isLeadEducator;
      const phone = control.value.phone;
      if (director || responsible || ba || lead) {
        return (phone !== null && phone !== '' && phone !== undefined) ? null : { phoneRequired: true };
      }
      return null;
    }
  }

  emailRequired(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const director = control.value.isDirector;
      const responsible = control.value.isResponsible;
      const ba = control.value.isBusinessAgent;
      const lead = control.value.isLeadEducator;
      const email = control.value.email;
      if (director || responsible || ba || lead) {
        return (email !== null && email !== '' && email !== undefined) ? null : { emailRequired: true };
      }
      return null;
    }
  }

  directorAttestationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const director = control.value.isDirector;
      const attestation = control.value.directorAttestation;
      if (director) {
        return (attestation) ? null : { directorAttested: true };
      }
      return null;
    };
  }

  instructorAttestationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const instructor = control.value.isInstructor;
      const attestation = control.value.instructorAttestation;
      if (instructor) {
        return (attestation) ? null : { instructorAttested: true };
      }
      return null;
    };
  }

  leadAttestationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const lead = control.value.isLeadEducator;
      const attestation = control.value.leadAttestation;
      if (lead) {
        return (attestation) ? null : { leadAttested: true };
      }
      return null;
    };
  }

  validateDirectorAttestation(): boolean {
    if (this.contactsForm.hasError('directorAttested') && this.contactsForm.get('directorAttestation').touched) {
      this.contactsForm.get('directorAttestation').setErrors(['required']);
      return true;
    }
    this.contactsForm.get('directorAttestation').clearValidators();
    this.contactsForm.get('directorAttestation').updateValueAndValidity();
    return false;
  }

  validateInstructorAttestation(): boolean {
    if (this.contactsForm.hasError('instructorAttested') && this.contactsForm.get('instructorAttestation').touched) {
      this.contactsForm.get('instructorAttestation').setErrors(['required']);
      return true;
    }
    this.contactsForm.get('instructorAttestation').clearValidators();
    this.contactsForm.get('instructorAttestation').updateValueAndValidity();
    return false;
  }

  validateLeadAttestation(): boolean {
    if (this.contactsForm.hasError('leadAttested') && this.contactsForm.get('leadAttestation').touched) {
      this.contactsForm.get('leadAttestation').setErrors(['required']);
      return true;
    }
    this.contactsForm.get('leadAttestation').clearValidators();
    this.contactsForm.get('leadAttestation').updateValueAndValidity();
    return false;
  }

  validatePhone(): boolean {
    if (this.contactsForm.hasError('phoneRequired') && this.contactsForm.get('phone').touched) {
      this.contactsForm.get('phone').setErrors(['required']);
      return true;
    }
    this.contactsForm.get('phone').clearValidators();
    this.contactsForm.get('phone').addValidators(Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/))
    this.contactsForm.get('phone').updateValueAndValidity();
    return false;
  }

  validateEmail(): boolean {
    if (this.contactsForm.hasError('emailRequired') && this.contactsForm.get('email').touched) {
      this.contactsForm.get('email').setErrors(['required']);
      return true;
    }
    this.contactsForm.get('email').clearValidators();
    this.contactsForm.get('email').addValidators(Validators.email);
    this.contactsForm.get('email').updateValueAndValidity();
    return false;
  }

  saveContact(): void {
    if (this.contactsForm.valid) {
      let form = this.contactsForm.value;
      this.contact.firstName = form.firstName;
      this.contact.lastName = form.lastName;
      this.contact.title = form.title;
      this.contact.email = form.email;
      this.contact.phone = form.phone;
      this.contact.isDirector = form.isDirector;
      this.contact.isResponsible = form.isResponsible;
      this.contact.isBusinessAgent = form.isBusinessAgent;
      this.contact.isLeadEducator = form.isLeadEducator;
      this.contact.isInstructor = form.isInstructor;
      this.contact.attestation = true;
      if (this.contact.id === 0) {
        this.addContact();
      }
      else {
        this.editContact();
      }
    }
  }

  addContact(): void {
    this.processing = true;
    this.trainingProgramService.addTrainingProgramContact(this.contact).subscribe(
      response => {
        this.contact = response;
        this.processing = false;
      },
      error => {
        this.sharedService.consoleLog(error);
        this.processing = false;
      },
      () => this.dialogRef.close(this.contact)
    );
  }

  editContact(): void {
    this.processing = true;
    this.trainingProgramService.editTrainingProgramContact(this.contact).subscribe(
      () => {
        this.dialogRef.close(this.contact);
        this.processing = false;
      },
      error => {
        this.sharedService.consoleLog(error);
      }
    );
  }

  cancel(): void {
    this.dialogRef.close('cancel');
  }
}

@Component({
    selector: 'program-revocation-dialog',
    templateUrl: 'dialog-program-revocation.html',
    styleUrls: ['./training-program.component.scss'],
    standalone: false
})

export class TrainingProgramRevocationDialog {

  revocationForm = new UntypedFormGroup({
    programComments: new UntypedFormControl('')
  });

  constructor(public dialogRef: MatDialogRef<ProgramContactDialog>,
    private trainingProgramService: TrainingProgramService,
    public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public revocation: ITrainingProgramRevocation) { }

  submitRevocationComment(): void {
    let formValues = this.revocationForm.value;
    this.revocation.programComments = formValues.programComments;
    this.trainingProgramService.submitRevocationComment(this.revocation).subscribe(
      () => this.dialogRef.close(this.revocation),
      error => this.sharedService.consoleLog(error)
    );
  }

  cancel(): void {
    this.dialogRef.close('cancel');
  }
}

@Component({
    selector: 'curriculum-update',
    templateUrl: './dialog-curriculum-update.html',
    styleUrls: ['./training-program.component.scss'],
    standalone: false
})

export class CurriculumUpdateDialog {
  public file: File = new File([''], 'empty');
  public commentsForm = new UntypedFormGroup({
    comments: new UntypedFormControl('', [Validators.required])
  });

  constructor(private trainingProgramService: TrainingProgramService,
    public dialogRef: MatDialogRef<CurriculumUpdateDialog>,
    public toastr: ToastrService,
    public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data: ICurriculumUpdateDialog) { }

  stageFile(event: Event): void {
    this.file = (event.target as HTMLInputElement).files[0];
    (event.target as HTMLInputElement).value = '';
  }

  saveFile(): void {
    if (this.commentsForm.valid && this.file.name !== 'empty') {
      let form = this.commentsForm.value;
      let upload: IDocument = {
        id: 0,
        name: this.file.name,
        comments: form.comments,
        extenstion: '',
        dateCreated: '',
        dateLastUpdated: '',
        createdBy: '',
        lastUpdatedBy: '',
        parentId: this.data.id,
        types: [this.data.curriculumUpdateTypeId],
        adminOnly: false,
        deprecated: false
      };
      const formData = new FormData();
      formData.append("file", this.file, this.file.name);
      formData.append("document", JSON.stringify(upload));
      this.trainingProgramService.uploadTrainingProgramUpdatedDocument(formData).subscribe(
        response => this.dialogRef.close(response),
        error => {
          this.sharedService.consoleLog(error);
          this.toastr.error('Unable to upload curriculum update');
        }
      );
    }
  }

  cancel(): void {
    this.dialogRef.close('cancel');
  }

}

@Component({
    selector: "training-program-snapshot-dialog",
    templateUrl: "training-program-snapshot.component.html",
    styleUrls: ["../../license-dashboard/license-dashboard.component.scss"],
    standalone: false
})

export class TrainingProgramSnapshotDialog {
  public curriculumMap: ICurriculumCoreElement[];
  public trainingProgram: ITrainingProgram;
  public curriculumPageKey: { [referenceId: string]: string };
  public curriculumTypeId: number = 0;
  public curriculumUpdateTypeId: number = 0;
  public deniedCurriculumTypeId: number = 0;
  public deprecatedCurriculumTypeId: number = 0;

  constructor(public dialogRef: MatDialogRef<TrainingProgramSnapshotDialog>, public sharedService: SharedService,
    private trainingProgramService: TrainingProgramService,
    @Inject(MAT_DIALOG_DATA) public data: ITrainingProgramSnapshotDialogData) {
    this.trainingProgram = data.trainingProgram;
    this.curriculumMap = data.curriculumMap;
    this.curriculumPageKey = data.curriculumPageKey;
  }

  ngOnInit() {
    this.trainingProgramService.getDocumentTypes().subscribe(
      response => {
        this.curriculumTypeId = response.find(dt => dt.type === 'Curriculum').id;
        this.curriculumUpdateTypeId = response.find(dt => dt.type === 'Curriculum Update').id;
        this.deniedCurriculumTypeId = response.find(dt => dt.type === 'Denied Curriculum').id;
        this.deprecatedCurriculumTypeId = response.find(dt => dt.type === 'Deprecated Curriculum').id;
      }, error => {this.sharedService.consoleLog(error)}

    );
  }

  getIndexLetter(index: number, upper: boolean): string {
    let letterArray: string[] = [
      'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
      'aa', 'bb', 'cc', 'dd', 'ee', 'ff', 'gg', 'hh', 'ii', 'jj', 'kk', 'll', 'mm', 'nn', 'oo', 'pp', 'qq', 'rr', 'ss', 'tt', 'uu', 'vv', 'ww', 'xx', 'yy', 'zz'
    ]
    if (upper) {
      return letterArray[index].toUpperCase();
    }
    return letterArray[index];
  }

  showFiveYear(applicationDate: string): boolean {
    const comparisonDate = new Date(2025, 0, 2);
    const parsedDate = new Date(applicationDate);

    if (applicationDate && parsedDate >= comparisonDate) {
      return false;
    }

    if (!applicationDate) {
      return false;
    }
    return parsedDate < comparisonDate;
  }

  showNewIncompleteDates(applicationDate: string): boolean {
      const jan1st2025 = new Date('2025-01-01');
      if (!applicationDate) {
        return true;
      }
      const parsedDate = new Date(applicationDate);
      return parsedDate > jan1st2025;
    }
}
